import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatScope } from '@/api/chat/chatApi.types';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { buildNameInitials } from '@/lib/nameInitials';
import { CHAT_MESSAGE_PAGE_SIZE } from '@/constants/chat';
import {
  useChatMessagesQuery,
  useLoadEarlierChatMessagesMutation,
  useMarkConversationReadMutation,
  useSendChatMessageMutation,
  type ChatMessageWithSendStatus,
} from '@/hooks/useChat';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { MessageBubble } from '@/components/chat/MessageBubble';

const EMPTY_MESSAGES: ChatMessageWithSendStatus[] = [];

interface PendingScrollAdjustment {
  previousScrollHeight: number;
  previousScrollTop: number;
}

interface ConversationScreenProps {
  scope: ChatScope;
  conversationId: string;
  counterpartName: string | null;
  fallbackCounterpartName: string;
  ownSenderKind: string;
  isReadOnly: boolean;
  readOnlyNotice: string | null;
  onBack: () => void;
}

export function ConversationScreen({
  scope,
  conversationId,
  counterpartName,
  fallbackCounterpartName,
  ownSenderKind,
  isReadOnly,
  readOnlyNotice,
  onBack,
}: ConversationScreenProps) {
  const { t } = useTranslation('chat');
  const messagesQuery = useChatMessagesQuery(scope, conversationId);
  const { mutate: markConversationRead } = useMarkConversationReadMutation(scope, conversationId);
  const retrySendMutation = useSendChatMessageMutation(scope, conversationId, ownSenderKind);
  const loadEarlierMessagesMutation = useLoadEarlierChatMessagesMutation(scope, conversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollAdjustmentRef = useRef<PendingScrollAdjustment | null>(null);
  const skipNextAutoScrollToBottomRef = useRef(false);
  const evaluatedInitialEarlierMessagesForConversationRef = useRef<string | null>(null);
  const [hasMoreEarlierMessages, setHasMoreEarlierMessages] = useState(true);
  const messages = messagesQuery.data === undefined ? EMPTY_MESSAGES : messagesQuery.data;
  const displayName =
    counterpartName === null || counterpartName.trim().length === 0
      ? fallbackCounterpartName
      : counterpartName;

  useEffect(
    function resetEarlierMessagesStateOnConversationChange() {
      setHasMoreEarlierMessages(true);
    },
    [conversationId]
  );

  useEffect(
    function evaluateInitialEarlierMessagesAvailability() {
      if (messagesQuery.isLoading === true) {
        return;
      }
      if (evaluatedInitialEarlierMessagesForConversationRef.current === conversationId) {
        return;
      }
      evaluatedInitialEarlierMessagesForConversationRef.current = conversationId;
      setHasMoreEarlierMessages(messages.length >= CHAT_MESSAGE_PAGE_SIZE);
    },
    [conversationId, messagesQuery.isLoading, messages]
  );

  useLayoutEffect(
    function manageScrollPositionOnMessagesChange() {
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer === null) {
        return;
      }
      const pendingAdjustment = pendingScrollAdjustmentRef.current;
      if (pendingAdjustment !== null) {
        scrollContainer.scrollTop =
          pendingAdjustment.previousScrollTop +
          (scrollContainer.scrollHeight - pendingAdjustment.previousScrollHeight);
        pendingScrollAdjustmentRef.current = null;
      }
    },
    [messages]
  );

  useEffect(
    function scrollToNewestMessage() {
      if (skipNextAutoScrollToBottomRef.current === true) {
        skipNextAutoScrollToBottomRef.current = false;
        return;
      }
      if (messagesEndRef.current !== null) {
        messagesEndRef.current.scrollIntoView({ block: 'end' });
      }
    },
    [messages.length]
  );

  useEffect(
    function markConversationReadOnNewIncomingMessage() {
      const newestMessage = messages[messages.length - 1];
      if (newestMessage === undefined) {
        return;
      }
      if (newestMessage.senderKind === ownSenderKind) {
        return;
      }
      markConversationRead();
    },
    [conversationId, messages, ownSenderKind, markConversationRead]
  );

  function retryFailedMessage(message: ChatMessageWithSendStatus) {
    retrySendMutation.mutate({
      tempId: message.id,
      body: message.body,
      attachmentIds: [],
    });
  }

  function loadEarlierMessages() {
    const oldestMessage = messages[0];
    if (oldestMessage === undefined) {
      return;
    }
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer !== null) {
      pendingScrollAdjustmentRef.current = {
        previousScrollHeight: scrollContainer.scrollHeight,
        previousScrollTop: scrollContainer.scrollTop,
      };
    }
    skipNextAutoScrollToBottomRef.current = true;
    loadEarlierMessagesMutation.mutate(oldestMessage.id, {
      onSuccess: function applyLoadEarlierResult(result) {
        setHasMoreEarlierMessages(result.hasMoreEarlierMessages);
      },
      onError: function revertSkipOnFailure() {
        skipNextAutoScrollToBottomRef.current = false;
        pendingScrollAdjustmentRef.current = null;
      },
    });
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-zoooffwhite">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zooborder bg-zoowhite">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={t('chat:conversation.backLabel')}
          onClick={onBack}
        >
          <Icon name="ArrowLeft" />
        </Button>
        <div className="w-9 h-9 rounded-full bg-zooprimary text-zoowhite flex items-center justify-center font-bold text-xs shrink-0">
          {buildNameInitials(displayName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-zooindigo truncate">{displayName}</div>
        </div>
        {isReadOnly === true ? (
          <Badge tone="muted" shape="tag" uppercase>
            {t('chat:conversation.readOnlyBadge')}
          </Badge>
        ) : null}
      </div>

      {isReadOnly === true && readOnlyNotice !== null ? (
        <div className="px-4 py-2 text-xs text-zoomuted bg-zoovpalelavender bg-opacity-40 border-b border-zooborder">
          {readOnlyNotice}
        </div>
      ) : null}

      <div
        ref={scrollContainerRef}
        data-testid="chatMessagesScrollContainer"
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3"
      >
        {messagesQuery.isLoading === true ? (
          <div className="py-10 flex justify-center">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-10">
            <p className="text-sm font-semibold text-zooindigo">
              {t('chat:conversation.emptyTitle')}
            </p>
            <p className="text-xs text-zoomuted mt-1">{t('chat:conversation.emptyBody')}</p>
          </div>
        ) : (
          <>
            {hasMoreEarlierMessages === true ? (
              <div className="flex justify-center pb-1">
                <Button
                  type="button"
                  variant="outline"
                  size="chip"
                  isLoading={loadEarlierMessagesMutation.isPending}
                  onClick={loadEarlierMessages}
                >
                  {t('chat:conversation.loadEarlierMessages')}
                </Button>
              </div>
            ) : null}
            {messages.map(function renderMessage(message) {
              return (
                <MessageBubble
                  key={message.id}
                  scope={scope}
                  message={message}
                  isOwnMessage={message.senderKind === ownSenderKind}
                  onRetry={retryFailedMessage}
                />
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatComposer
        scope={scope}
        conversationId={conversationId}
        ownSenderKind={ownSenderKind}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}
